import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  reset_at: string;
  retry_after: number;
}

interface RateLimitState {
  isLimited: boolean;
  remaining: number;
  retryAfter: number;
  resetAt: Date | null;
}

export const useRateLimit = () => {
  const [state, setState] = useState<RateLimitState>({
    isLimited: false,
    remaining: -1,
    retryAfter: 0,
    resetAt: null,
  });

  const checkRateLimit = async (
    action: "login" | "signup" | "reset" | "public_report",
    identifier: string
  ): Promise<{ allowed: boolean; message?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke<RateLimitResult>(
        "check-rate-limit",
        {
          body: { action, identifier },
        }
      );

      if (error) {
        // If rate limit check fails, allow the action (fail open)
        logger.error("Rate limit check failed:", error);
        return { allowed: true };
      }

      if (!data) {
        return { allowed: true };
      }

      setState({
        isLimited: !data.allowed,
        remaining: data.remaining,
        retryAfter: data.retry_after,
        resetAt: new Date(data.reset_at),
      });

      if (!data.allowed) {
        const minutes = Math.ceil(data.retry_after / 60);
        return {
          allowed: false,
          message: `Too many attempts. Please try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`,
        };
      }

      return { allowed: true };
    } catch (err) {
      // Fail open - if rate limiting service is down, allow the request
      logger.error("Rate limit check error:", err);
      return { allowed: true };
    }
  };

  const formatRetryTime = () => {
    if (state.retryAfter <= 0) return "";

    const minutes = Math.floor(state.retryAfter / 60);
    const seconds = state.retryAfter % 60;

    if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? "" : "s"}`;
    }
    return `${seconds} second${seconds === 1 ? "" : "s"}`;
  };

  const clearLimitState = () => {
    setState({
      isLimited: false,
      remaining: -1,
      retryAfter: 0,
      resetAt: null,
    });
  };

  return {
    checkRateLimit,
    isLimited: state.isLimited,
    remaining: state.remaining,
    retryAfter: state.retryAfter,
    resetAt: state.resetAt,
    formatRetryTime,
    clearLimitState,
  };
};
